import { useState, useEffect, useCallback } from 'react'
import { NotificacoesService } from '@/services'
import { Notificacao } from '@/types'

interface UseNotificacoesReturn {
  notificacoes: Notificacao[]
  notificacoesNaoLidas: Notificacao[]
  totalNaoLidas: number
  loading: boolean
  error: string | null
  carregarNotificacoes: () => Promise<void>
  carregarNaoLidas: () => Promise<void>
  marcarComoLida: (id: number) => Promise<void>
  marcarTodasComoLidas: () => Promise<void>
  deletarNotificacao: (id: number) => Promise<void>
  atualizarContador: () => Promise<void>
}

export function useNotificacoes(): UseNotificacoesReturn {
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([])
  const [notificacoesNaoLidas, setNotificacoesNaoLidas] = useState<Notificacao[]>([])
  const [totalNaoLidas, setTotalNaoLidas] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Carregar todas as notificações
  const carregarNotificacoes = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await NotificacoesService.getNotificacoes(50)
      
      if (response.success) {
        setNotificacoes(response.notificacoes)
      } else {
        setError('Erro ao carregar notificações')
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar notificações')
    } finally {
      setLoading(false)
    }
  }, [])

  // Carregar apenas notificações não lidas
  const carregarNaoLidas = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await NotificacoesService.getNotificacoesNaoLidas(20)
      
      if (response.success) {
        setNotificacoesNaoLidas(response.notificacoes)
      } else {
        setError('Erro ao carregar notificações não lidas')
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar notificações não lidas')
    } finally {
      setLoading(false)
    }
  }, [])

  // Atualizar contador de notificações não lidas
  const atualizarContador = useCallback(async () => {
    try {
      const response = await NotificacoesService.contarNotificacoesNaoLidas()
      
      if (response.success) {
        setTotalNaoLidas(response.total)
      }
    } catch (err: any) {
      console.error('Erro ao atualizar contador:', err)
    }
  }, [])

  // Marcar notificação como lida
  const marcarComoLida = useCallback(async (id: number) => {
    try {
      const response = await NotificacoesService.marcarNotificacaoComoLida(id)
      
      if (response.success) {
        // Atualizar estado local
        setNotificacoes(prev => 
          prev.map(n => n.id === id ? { ...n, lida: true } : n)
        )
        setNotificacoesNaoLidas(prev => 
          prev.filter(n => n.id !== id)
        )
        
        // Atualizar contador
        await atualizarContador()
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao marcar notificação como lida')
    }
  }, [atualizarContador])

  // Marcar todas as notificações como lidas
  const marcarTodasComoLidas = useCallback(async () => {
    try {
      const response = await NotificacoesService.marcarTodasNotificacoesComoLidas()
      
      if (response.success) {
        // Atualizar estado local
        setNotificacoes(prev => 
          prev.map(n => ({ ...n, lida: true }))
        )
        setNotificacoesNaoLidas([])
        setTotalNaoLidas(0)
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao marcar todas as notificações como lidas')
    }
  }, [])

  // Deletar notificação
  const deletarNotificacao = useCallback(async (id: number) => {
    try {
      const response = await NotificacoesService.deletarNotificacao(id)
      
      if (response.success) {
        // Remover das listas locais
        setNotificacoes(prev => prev.filter(n => n.id !== id))
        setNotificacoesNaoLidas(prev => prev.filter(n => n.id !== id))
        
        // Atualizar contador
        await atualizarContador()
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao deletar notificação')
    }
  }, [atualizarContador])

  // Carregar dados iniciais
  useEffect(() => {
    carregarNotificacoes()
    carregarNaoLidas()
    atualizarContador()
  }, [carregarNotificacoes, carregarNaoLidas, atualizarContador])

  // Atualizar contador a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(atualizarContador, 30000)
    return () => clearInterval(interval)
  }, [atualizarContador])

  return {
    notificacoes,
    notificacoesNaoLidas,
    totalNaoLidas,
    loading,
    error,
    carregarNotificacoes,
    carregarNaoLidas,
    marcarComoLida,
    marcarTodasComoLidas,
    deletarNotificacao,
    atualizarContador
  }
}
